import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  clearAssistants,
  getAllAssistants,
} from "../../features/assistant/assistantSlice";
import moment from "moment/moment";
import NotFoundMessage from "../../components/common/Modal/NotFoundMessage";
import LoadingComponent from "../../components/common/LoadingComponent";
import {
  capitalize,
  MODEL_ICONS,
  ICONS,
} from "../../constants/constants";
import { Icons } from "../../constants/icon-library";

const AssistantList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { assistants, loading } = useSelector((state) => state.assistant);
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(clearAssistants());
    if (user?.org) {
      dispatch(getAllAssistants(user.org));
    }
  }, [user?.org, dispatch]);

  const showAssistantsNotFoundError = !loading && assistants.length === 0;
  return (
    <div className="content">
      <div className="d-flex flex-column pt-3">
        {!showAssistantsNotFoundError && (
          <>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-primary mb-1"
                onClick={() => navigate(`create`)}
              >
                <i
                  className={`wb ${Icons("add")} fs-8 me-2 mt-3 align-middle`}
                ></i>
                Create Assistant
              </button>
            </div>
            <div className="">
              <div className="w-100" data-breakpoint="lg">
                <div className="navbar-vertical-content">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-uppercase h4 text-body-tertiary text-opacity-85 m-0 fw-bold">
                      <i
                        className={`wb ${Icons("system-activity")} fs-7 me-2 `}
                      ></i>
                      Assistants
                    </div>
                  </div>

                  {loading ? (
                    <LoadingComponent loading={true} />
                  ) : assistants.length === 0 ? (
                    <p className="text-danger small fst-italic">
                      No assistants found. Please add a new assistant.
                    </p>
                  ) : (
                    <div className="d-flex mt-4">
                      <div className="row">
                        {[...assistants].reverse().map((assistant) => (
                          <div
                            className="assistant-card col-auto d-flex flex-column m-2 p-4 rounded justify-content-between"
                            key={assistant.uuid}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="h3 align-self-center">
                                {assistant.name}
                              </div>
                            </div>

                            <div className="my-2 border-top">
                              <div className="d-flex mt-2 align-items-center">
                                <i
                                  className={`wb ${Icons("clock")} fs-8 me-2 `}
                                ></i>
                                {moment
                                  .utc(assistant.createdAt)
                                  .format("MMM D, YYYY • h:mm A")}
                              </div>
                              <div className="d-flex mt-2 align-items-center">
                                {(() => {
                                  const name = capitalize(
                                    assistant.platform?.type,
                                  );
                                  const key = name?.toLowerCase();
                                  const icon = ICONS[key];
                                  return (
                                    <>
                                      {icon ? (
                                        <img
                                          src={icon}
                                          alt={key}
                                          style={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                          }}
                                          className="me-2"
                                        />
                                      ) : (
                                        <i
                                          className={`wb ${Icons(
                                            "codesandbox",
                                          )} fs-8 me-2 `}
                                        ></i>
                                      )}
                                      <span>{name || "No Platform"}</span>
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="d-flex mt-2 align-items-center">
                                {(() => {
                                  const name = capitalize(
                                    assistant.chatConfiguration?.name?.toLowerCase(),
                                  );
                                  const key =
                                    assistant.chatConfiguration?.type?.toLowerCase();
                                  const icon = MODEL_ICONS[key];
                                  return (
                                    <>
                                      {icon ? (
                                        <img
                                          src={icon}
                                          alt={key}
                                          style={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                          }}
                                          className="me-2"
                                        />
                                      ) : (
                                        <i
                                          className={`wb ${Icons(
                                            "cpu",
                                          )} fs-8 me-2 `}
                                        ></i>
                                      )}
                                      <span>{name || "No Model"}</span>
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="d-flex mt-2 align-items-center">
                                {(() => {
                                  const name = capitalize(
                                    assistant.voiceConfiguration?.name?.toLowerCase(),
                                  );
                                  const key =
                                    assistant.voiceConfiguration?.type?.toLowerCase();
                                  const icon = MODEL_ICONS[key];
                                  return (
                                    <>
                                      {icon ? (
                                        <img
                                          src={icon}
                                          alt={key}
                                          style={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                          }}
                                          className="me-2"
                                        />
                                      ) : (
                                        <i
                                          className={`wb ${Icons(
                                            "cpu",
                                          )} fs-8 me-2 `}
                                        ></i>
                                      )}
                                      <span>{name || "No Model"}</span>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="mt-3">
                              <div className="d-flex justify-content-end align-items-center">
                                <button
                                  className="btn btn-primary px-2"
                                  onClick={() => navigate(`${assistant.uuid}`)}
                                >
                                  <div className="d-flex p-0">
                                    <i
                                      className={`wb ${Icons(
                                        "constraints",
                                      )} fs-8 me-2 `}
                                    ></i>
                                    <span className="text-nowrap align-self-center">
                                      Configuration
                                    </span>
                                  </div>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {showAssistantsNotFoundError && (
          <NotFoundMessage
            message="No assistant found. Please create a new assistant."
            onAddNew={() => navigate(`create`)}
            buttonMessage="Create Assistant"
          />
        )}
      </div>
    </div>
  );
};

export default AssistantList;
